import { useMemo } from 'react';

import { TaxonomyType } from '@revelio/data-access';

import type { TreeData } from '../../components/tree/useTreeApi';
import { RICS_TAXONOMY_ID } from '../constants';
import type { TaxonomyLevelMetadata } from '../utils/taxonomyTree';
import { useTaxonomyTree } from './useTaxonomyTree';

/**
 * RICS k10/k50 `id => label` maps sourced from the taxonomyTree API. The
 * taxonomy-preference selects (role + skill) only need label lookup — building
 * the dropdown options and resolving a saved id to its name — so the tree
 * collapses straight to maps instead of reconstructing the legacy
 * selection-list shape.
 */
export const useRicsTaxonomyLabels = ({
  pause = false,
}: {
  pause?: boolean;
} = {}): {
  labels: RicsLabelMaps | null;
  fetching: boolean;
} => {
  const { treeData, levels, fetching } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Rics,
    taxonomyId: RICS_TAXONOMY_ID,
    levels: [1, 2], // RICS k10 + k50
    pause,
  });

  const labels = useMemo(
    () =>
      treeData.length === 0 ? null : ricsTreeToLabelMaps(treeData, levels),
    [treeData, levels]
  );

  return { labels, fetching };
};

/**
 * RICS k10/k50 label maps: `id => label`, one map per level. Label lookup is all
 * the taxonomy-preference selects need (build options + resolve a saved id to its
 * name), so the tree collapses straight to maps — no intermediate selection-list
 * shape. Node depth picks the level: levels sorted ascending by `id` give depth 0
 * -> rics_k10, depth 1 -> rics_k50.
 */
export type RicsLabelMaps = {
  rics_k10: Map<number, string>;
  rics_k50: Map<number, string>;
};

const ricsTreeToLabelMaps = (
  treeData: readonly TreeData[],
  levels: readonly TaxonomyLevelMetadata[]
): RicsLabelMaps => {
  const tagByDepth = [...levels]
    .sort((a, b) => a.id - b.id)
    .map((level) => level.tag ?? null);

  const result: RicsLabelMaps = {
    rics_k10: new Map(),
    rics_k50: new Map(),
  };

  const visit = (nodes: readonly TreeData[]): void => {
    for (const node of nodes) {
      const tag = tagByDepth[node.depth ?? 0];
      if (tag === 'rics_k10' || tag === 'rics_k50') {
        const id = Number(node.id);
        if (!result[tag].has(id)) {
          result[tag].set(id, node.name);
        }
      }
      if (node.children) {
        visit(node.children);
      }
    }
  };

  visit(treeData);
  return result;
};
